import { useEffect, useState, useRef, memo, useCallback } from "react";
import stripePromise from "../lib/stripe";
import { API_ENDPOINTS } from "../config/api";
import { DatabaseService } from "../services/database";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { IdentityService } from "../services/identity";
import { ProfileCacheService } from "../services/profileCache";
import { useQueryClient } from "@tanstack/react-query";
import {
  prefetchDashboardData,
  prefetchReviewerDashboardData,
} from "../hooks/useQuery";
import { Mail } from "lucide-react";
import ContactModal from "./ContactUsModal";
import AnimatedLogo from "./AnimatedLogo";
import BecomeReviewerModal from "./BecomeReviewerModal";
import PlanUpgradeModal from "./PlanUpgradeModal";
import { Button } from "./ui/button";
import { getAvatarColorClasses } from "../lib/utils";

const useScrollToHash = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Minimal delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const hash = location.hash.slice(1);
        const element = document.getElementById(hash);

        if (element) {
          // Find the scrollable container (the div with overflow-y-auto in Layout)
          const scrollContainer = document.querySelector(
            ".overflow-y-auto"
          ) as HTMLElement;

          if (scrollContainer) {
            // Try to find the title/heading within the section to scroll to it directly
            const titleElement = element.querySelector(
              "h1, h2, h3, h4, h5, h6"
            ) as HTMLElement;
            const targetElement = titleElement || element;

            // Calculate position relative to scroll container
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = targetElement.getBoundingClientRect();

            // Different offsets for different sections
            let navbarHeight = 90; // Default offset
            if (hash === "faq-section") {
              navbarHeight = 150;
            } else if (hash === "pricing-section") {
              navbarHeight = 90;
            }

            // Current scroll position + element position relative to container - navbar
            // This positions the title right below the navbar at the top of the screen
            const currentScroll = scrollContainer.scrollTop;
            const elementOffsetFromContainer =
              elementRect.top - containerRect.top;
            const targetScroll =
              currentScroll + elementOffsetFromContainer - navbarHeight;

            scrollContainer.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: "smooth",
            });
          } else {
            // Fallback: use scrollIntoView
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        } else {
          console.warn(`Element with id "${hash}" not found`);
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [location]);
};

function Navbar({
  user,
  onReviewClick,
  onOpenReviewerOnboarding,
}: {
  user: any;
  onReviewClick: () => void;
  onOpenReviewerOnboarding?: () => void;
}) {
  // Preload components on hover
  const preloadComponent = useCallback((componentPath: string) => {
    if (componentPath === "/applicant-dashboard") {
      import("../components/ApplicantDashboard");
    } else if (componentPath === "/reviewer-dashboard") {
      import("../components/ReviewerDashboard");
    } else if (componentPath === "/scholarships") {
      import("../components/Scholarships");
    } else if (componentPath === "/colleges") {
      import("../components/Colleges");
    } else if (componentPath === "/match") {
      import("../components/MatchPage");
    }
  }, []);
  const queryClient = useQueryClient();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isReviewerModalOpen, setIsReviewerModalOpen] = useState(false);
  const [isPlanUpgradeModalOpen, setIsPlanUpgradeModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check for reviewer intent on mount
  useEffect(() => {
    if (user && onOpenReviewerOnboarding) {
      try {
        const intent = localStorage.getItem("postLoginIntent");
        if (intent === "reviewer") {
          localStorage.removeItem("postLoginIntent");
          onOpenReviewerOnboarding();
        }
      } catch (_) {}
    }
  }, [user, onOpenReviewerOnboarding]);

  const {
    signOut,
    loading: authLoading,
    subscriptionData,
    subscriptionLoading,
    userOnboarded,
    cachedProfile,
    identity,
    identityLoading,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  useScrollToHash();

  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      const response = await fetch(API_ENDPOINTS.createPortalSession, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: user.email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to create portal session");
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
    }
  };

  const handleUpgradeClick = async () => {
    if (!user) return;

    try {
      const response = await fetch(API_ENDPOINTS.createCheckoutSession, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          line_items: [
            {
              price: process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to create checkout session"
        );
      }

      const { sessionId } = await response.json();

      const stripe = await stripePromise;
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          throw error;
        }
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
    }
  };

  const [isLogoHovered, setIsLogoHovered] = useState(false);

  const renderLeftSide = () => {
    return (
      <div
        className="flex items-center gap-2 text-lg sm:text-xl font-semibold cursor-pointer overflow-visible"
        onClick={() => navigate("/")}
        onMouseEnter={() => setIsLogoHovered(true)}
        onMouseLeave={() => setIsLogoHovered(false)}
      >
        <div className="overflow-visible py-3">
          <AnimatedLogo
            className="w-5 h-5 sm:w-6 sm:h-6"
            isHovered={isLogoHovered}
          />
        </div>
        <span>CampusQuest</span>
      </div>
    );
  };

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null); // Create a ref for the menu
  const mobileMenuRef = useRef<HTMLDivElement>(null); // Create a ref for mobile menu

  // Close menu if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen || isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileMenuOpen, isMobileMenuOpen]);

  const renderProfileMenu = () => {
    if (!user) return null;
    const displayName =
      cachedProfile?.name || user?.name || user?.email || "User";
    const initials = displayName
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    const avatarColorClasses = getAvatarColorClasses(displayName);

    return (
      <div className="relative" ref={profileMenuRef}>
        <button
          onClick={() => setIsProfileMenuOpen((v) => !v)}
          className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm focus:outline-none focus:ring-0 focus-visible:outline-none overflow-hidden ${
            cachedProfile?.profile_image_url
              ? "bg-gray-200 border border-gray-300"
              : avatarColorClasses
          }`}
          aria-label="Open profile menu"
        >
          {cachedProfile?.profile_image_url ? (
            <img
              src={cachedProfile.profile_image_url}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement!.innerHTML = initials;
              }}
            />
          ) : (
            initials
          )}
        </button>
        <div
          className={`absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 origin-top-right transition-all duration-200 ease-out ${
            isProfileMenuOpen
              ? "opacity-100 scale-100"
              : "opacity-0 scale-95 pointer-events-none"
          }`}
        >
          {/* Greeting Section */}
          <div className="px-4 py-2">
            <p className="text-sm font-medium text-gray-900">
              Hi, {displayName.split(" ")[0]}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <div className="my-2 h-px bg-gray-200" />
          {/* Manage Profile Section */}
          <div className="px-4 py-1 text-xs uppercase tracking-wide text-gray-400">
            Manage my Profile
          </div>
          <button
            onClick={() => {
              setIsProfileMenuOpen(false);
              // Check if we're on a page with sidebar and activate profile tab
              if (pathname === "/reviewer-dashboard") {
                // For dashboard, set profile section active via global function
                if ((window as any).setDashboardActiveSection) {
                  (window as any).setDashboardActiveSection("profile");
                }
              } else if (pathname === "/applicant-dashboard") {
                // For my-essays, set profile section active via global function
                if ((window as any).setApplicantDashboardActiveSection) {
                  (window as any).setApplicantDashboardActiveSection("profile");
                }
              } else {
                // If not on a sidebar page, navigate to appropriate page with profile
                // Default to dashboard for reviewers, my-essays for others
                if (identity?.isReviewer) {
                  navigate("/reviewer-dashboard");
                  // Set profile active after navigation
                  setTimeout(() => {
                    if ((window as any).setDashboardActiveSection) {
                      (window as any).setDashboardActiveSection("profile");
                    }
                  }, 100);
                } else {
                  navigate("/applicant-dashboard");
                  // Set profile active after navigation
                  setTimeout(() => {
                    if ((window as any).setApplicantDashboardActiveSection) {
                      (window as any).setApplicantDashboardActiveSection(
                        "profile"
                      );
                    }
                  }, 100);
                }
              }
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
              />
            </svg>
            Open Profile
          </button>
          {subscriptionData?.plan === "pro" && (
            <>
              <div className="my-2 h-px bg-gray-100" />
              <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-500">
                Subscription
              </div>
              <button
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  handleManageSubscription();
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
                  />
                </svg>
                Manage Subscription
              </button>
            </>
          )}
          <div className="my-2 h-px bg-gray-200" />
          <div className="px-3 py-2">
            <button
              onClick={() => {
                setIsProfileMenuOpen(false);
                signOut();
              }}
              className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Essay count state for plan upgrade modal (cached separately for performance)
  const [userEssayCount, setUserEssayCount] = useState<number>(0);

  // Analysis count state for plan upgrade modal (cached separately for performance)
  const [userAnalysisCount, setUserAnalysisCount] = useState<number>(0);

  useEffect(() => {
    const loadMetricsData = async () => {
      if (user?.id) {
        // Check cache for metrics first
        const cachedMetrics = ProfileCacheService.getCachedMetrics(user.id);
        if (cachedMetrics) {
          setUserEssayCount(cachedMetrics.essayCount || 0);
          setUserAnalysisCount(cachedMetrics.analysisCount || 0);
        }

        // If no cache or cache is stale, fetch fresh data
        if (!cachedMetrics) {
          try {
            // Fetch essay count for plan upgrade modal
            const essays = await DatabaseService.getUserEssays(user.id);
            const essayCount = essays.length;

            // Fetch analysis count for plan upgrade modal
            const analysisCount = await DatabaseService.getUserAnalysisCount(
              user.id
            );

            // Cache the metrics
            ProfileCacheService.setCachedMetrics(user.id, {
              essayCount,
              analysisCount,
            });

            setUserEssayCount(essayCount);
            setUserAnalysisCount(analysisCount);
          } catch (error) {
            console.error("Error fetching user metrics:", error);
          }
        }
      } else {
        setUserEssayCount(0);
        setUserAnalysisCount(0);
      }
    };

    loadMetricsData();
  }, [user?.id, userOnboarded]); // Also re-check when onboarding status changes

  // Expose reviewer status globally for navbar profile logic
  useEffect(() => {
    if (identity) {
      (window as any).isUserReviewer = identity.isReviewer;
    }
  }, [identity]);

  const renderMobileMenu = () => {
    const isLandingPage =
      pathname === "/" ||
      pathname.startsWith("/share/") ||
      pathname === "/signup" ||
      pathname === "/privacy" ||
      pathname === "/terms";

    return (
      <div className="relative md:hidden" ref={mobileMenuRef}>
        {/* Hamburger Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Toggle mobile menu"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute right-0 top-12 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            {/* Free plan badge - only show for free users who are not reviewers */}
            {user &&
              subscriptionData?.plan !== "pro" &&
              !identity?.isReviewer && (
                <button
                  onClick={() => {
                    setIsPlanUpgradeModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 bg-gray-900 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    Free plan
                  </div>
                </button>
              )}

            <Link
              to="/#faq-section"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              FAQ
            </Link>
            <Link
              to="/#pricing-section"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/match"
              onClick={() => setIsMobileMenuOpen(false)}
              onMouseEnter={() => preloadComponent("/match")}
              className="block px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Match
            </Link>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                // Redirect signed-in applicants to dashboard browse tab
                if (user && !identity?.isReviewer) {
                  navigate(
                    "/applicant-dashboard?tab=your-scholarships&scholarshipsTab=saved"
                  );
                } else {
                  navigate("/scholarships");
                }
              }}
              className="w-full text-left block px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Scholarships
            </button>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                // Redirect signed-in applicants to dashboard browse tab
                if (user && !identity?.isReviewer) {
                  navigate(
                    "/applicant-dashboard?tab=your-colleges&collegesTab=saved"
                  );
                } else {
                  navigate("/colleges");
                }
              }}
              onMouseEnter={() => preloadComponent("/colleges")}
              className="w-full text-left block px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Colleges
            </button>
            <button
              onClick={() => {
                setIsContactModalOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Contact
            </button>

            {/* Show navigation button based on page context */}
            {isLandingPage && (
              <>
                <div className="border-t border-gray-100 my-2"></div>
                {user ? (
                  <>
                    {!identityLoading && (
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          if (userOnboarded === false) {
                            navigate("/onboarding");
                          } else {
                            if (identity?.isReviewer) {
                              navigate("/reviewer-dashboard");
                            } else {
                              navigate("/applicant-dashboard");
                            }
                          }
                        }}
                        className="w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                      >
                        {userOnboarded === false
                          ? "Complete Onboarding"
                          : "Dashboard"}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onReviewClick();
                    }}
                    className="w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Get Feedback
                  </button>
                )}
              </>
            )}

            {/* Profile menu options for logged-in users */}
            {user && (
              <>
                <div className="border-t border-gray-100 my-2"></div>
                <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-500">
                  My Account
                </div>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Check if we're on a page with sidebar and activate profile tab
                    if (pathname === "/reviewer-dashboard") {
                      if ((window as any).setDashboardActiveSection) {
                        (window as any).setDashboardActiveSection("profile");
                      }
                    } else if (pathname === "/applicant-dashboard") {
                      if ((window as any).setApplicantDashboardActiveSection) {
                        (window as any).setApplicantDashboardActiveSection(
                          "profile"
                        );
                      }
                    } else {
                      // Navigate to appropriate page with profile
                      if (identity?.isReviewer) {
                        navigate("/reviewer-dashboard");
                        setTimeout(() => {
                          if ((window as any).setDashboardActiveSection) {
                            (window as any).setDashboardActiveSection(
                              "profile"
                            );
                          }
                        }, 100);
                      } else {
                        navigate("/applicant-dashboard");
                        setTimeout(() => {
                          if (
                            (window as any).setApplicantDashboardActiveSection
                          ) {
                            (window as any).setApplicantDashboardActiveSection(
                              "profile"
                            );
                          }
                        }, 100);
                      }
                    }
                  }}
                  className="w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  Open Profile
                </button>
                {subscriptionData?.plan === "pro" && (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleManageSubscription();
                    }}
                    className="w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
                      />
                    </svg>
                    Manage Subscription
                  </button>
                )}
                <div className="border-t border-gray-100 my-2"></div>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    signOut();
                  }}
                  className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRightSide = () => {
    if (authLoading || subscriptionLoading) {
      return (
        <div className="flex items-center gap-3 sm:gap-6">
          <div className="w-16 sm:w-24 h-9 bg-gray-100 animate-pulse rounded-lg"></div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 sm:gap-6">
        {/* Desktop Navigation - Hidden on mobile */}
        <div className="hidden md:flex items-center gap-3 sm:gap-6">
          {/* Free plan badge - only show for free users who are not reviewers */}
          {user &&
            subscriptionData?.plan !== "pro" &&
            !identity?.isReviewer && (
              <button
                onClick={() => setIsPlanUpgradeModalOpen(true)}
                className="flex items-center gap-2 bg-gray-900 text-white px-3 py-1.5 rounded-full text-xs font-medium hover:bg-gray-800 transition-colors"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Free plan
              </button>
            )}
          <Link
            to="/#faq-section"
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
          >
            FAQ
          </Link>
          <Link
            to="/#pricing-section"
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
          >
            Pricing
          </Link>
          <Link
            to="/match"
            onMouseEnter={() => preloadComponent("/match")}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
          >
            Match
          </Link>
          <button
            onClick={() => {
              // Redirect signed-in applicants to dashboard browse tab
              if (user && !identity?.isReviewer) {
                navigate(
                  "/applicant-dashboard?tab=your-scholarships&scholarshipsTab=saved"
                );
              } else {
                navigate("/scholarships");
              }
            }}
            onMouseEnter={() => preloadComponent("/scholarships")}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
          >
            Scholarships
          </button>
          <button
            onClick={() => {
              // Redirect signed-in applicants to dashboard browse tab
              if (user && !identity?.isReviewer) {
                navigate(
                  "/applicant-dashboard?tab=your-colleges&collegesTab=saved"
                );
              } else {
                navigate("/colleges");
              }
            }}
            onMouseEnter={() => preloadComponent("/colleges")}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
          >
            Colleges
          </button>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 text-sm sm:text-base px-3 py-2 rounded-lg"
            data-contact-button
          >
            Contact
          </button>
          {user ? (
            <>
              {!identityLoading && (
                <Button
                  onClick={() => {
                    if (userOnboarded === false) {
                      navigate("/onboarding");
                    } else {
                      navigate(
                        identity?.isReviewer
                          ? "/reviewer-dashboard"
                          : "/applicant-dashboard"
                      );
                    }
                  }}
                  onMouseEnter={() => {
                    // Prefetch appropriate dashboard data when hovering over the navigation button
                    if (user?.id && userOnboarded !== false) {
                      if (identity?.isReviewer) {
                        prefetchReviewerDashboardData(queryClient, user.id);
                      } else {
                        prefetchDashboardData(queryClient, user.id);
                      }
                    }
                  }}
                  className="bg-black text-white hover:bg-gray-800 px-2 sm:px-4 text-sm sm:text-base whitespace-nowrap"
                >
                  {userOnboarded === false
                    ? "Complete Onboarding"
                    : "Dashboard"}
                </Button>
              )}
            </>
          ) : (
            <Button
              onClick={onReviewClick}
              className="bg-black text-white hover:bg-gray-800 px-2 sm:px-4 text-sm sm:text-base whitespace-nowrap"
            >
              Get Feedback
            </Button>
          )}
        </div>

        {/* Mobile: Hamburger menu only (profile options inside) */}
        <div className="md:hidden">{renderMobileMenu()}</div>

        {/* Desktop: Profile menu */}
        <div className="hidden md:block">{user && renderProfileMenu()}</div>
      </div>
    );
  };

  return (
    <nav className="fixed w-full bg-white/80 backdrop-blur-sm z-50 h-[64px] sm:h-[72px] border-b border-gray-200 px-3 sm:px-6">
      <div className="h-full flex items-center justify-between">
        <div className="flex-shrink-0">{renderLeftSide()}</div>
        <div className="min-h-[40px] flex items-center flex-shrink-0">
          {renderRightSide()}
        </div>
      </div>
      <BecomeReviewerModal
        open={isReviewerModalOpen}
        onOpenChange={setIsReviewerModalOpen}
        onStartAuth={() => {
          try {
            localStorage.setItem("postLoginIntent", "reviewer");
          } catch (_) {}
          // Reuse landing auth modal flow
          onReviewClick();
        }}
        isLoggedIn={!!user}
        onGoToOnboarding={() => {
          setIsReviewerModalOpen(false);
          onOpenReviewerOnboarding?.();
        }}
      />
      <PlanUpgradeModal
        open={isPlanUpgradeModalOpen}
        onOpenChange={setIsPlanUpgradeModalOpen}
        onUpgrade={handleUpgradeClick}
        currentUsage={{
          essays: userEssayCount,
          maxEssays: 5,
          analyses: userAnalysisCount,
          maxAnalyses: 5,
        }}
      />
      <ContactModal
        open={isContactModalOpen}
        onOpenChange={setIsContactModalOpen}
      />
    </nav>
  );
}

export default memo(Navbar);
